// @tier: PREMIUM — Rapports & Analyses
import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import unifiedApi from '../services/unifiedApi';
import { toast } from 'sonner';

export const REPORT_COLORS = ['#10B981', '#F59E0B', '#EF4444', '#3B82F6', '#8B5CF6'];

// Conservé pour frontDashboard.jsx (graphique camembert incidents) — fallback si by_type absent
export const INCIDENT_TYPES = [
  { name: 'Pannes', value: 0 },
  { name: 'Retards', value: 0 },
  { name: 'Accidents', value: 0 },
  { name: 'Autres', value: 0 },
];

export const useReports = () => {
  const queryClient = useQueryClient();
  const [dateRange, setDateRange] = useState({
    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    to: new Date(),
  });
  const [selectedCircuit, setSelectedCircuit] = useState('all');

  const params = {
    startDate: dateRange.from?.toISOString().split('T')[0],
    endDate: dateRange.to?.toISOString().split('T')[0],
    ...(selectedCircuit !== 'all' && { routeId: selectedCircuit }),
  };

  const { data: punctualityData, isLoading: loadingPunctuality } = useQuery({
    queryKey: ['reports', 'punctuality', params],
    queryFn: async () => {
      const r = await unifiedApi.reports.getPunctuality(params);
      if (!r.success) throw new Error(r.error || 'Erreur ponctualité');
      return r.data;
    },
  });

  const { data: attendanceData, isLoading: loadingAttendance } = useQuery({
    queryKey: ['reports', 'attendance', params],
    queryFn: async () => {
      const r = await unifiedApi.reports.getAttendance(params);
      if (!r.success) throw new Error(r.error || 'Erreur présences');
      return r.data;
    },
  });

  const { data: driversData, isLoading: loadingDrivers } = useQuery({
    queryKey: ['reports', 'drivers', params],
    queryFn: async () => {
      const r = await unifiedApi.reports.getDrivers({ startDate: params.startDate, endDate: params.endDate });
      if (!r.success) throw new Error(r.error || 'Erreur chauffeurs');
      return r.data;
    },
  });

  const { data: incidentsData, isLoading: loadingIncidents } = useQuery({
    queryKey: ['reports', 'incidents', params],
    queryFn: async () => {
      const r = await unifiedApi.reports.getIncidents(params);
      if (!r.success) throw new Error(r.error || 'Erreur incidents');
      return r.data;
    },
  });

  // type: 'punctuality' | 'attendance' | 'drivers' | 'incidents' (VALID_TYPES, reports.routes.js)
  const handleExportPDF = useCallback(
    async (type) => {
      try {
        await unifiedApi.reports.exportFile(type, 'pdf', { startDate: params.startDate, endDate: params.endDate });
        toast.success('Export PDF téléchargé');
      } catch (err) {
        toast.error(err.message || "Erreur lors de l'export PDF");
      }
    },
    [params.startDate, params.endDate],
  );

  const handleExportExcel = useCallback(
    async (type) => {
      try {
        await unifiedApi.reports.exportFile(type, 'excel', { startDate: params.startDate, endDate: params.endDate });
        toast.success('Export Excel téléchargé');
      } catch (err) {
        toast.error(err.message || "Erreur lors de l'export Excel");
      }
    },
    [params.startDate, params.endDate],
  );

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['reports'] });
  }, [queryClient]);

  const loading = loadingPunctuality || loadingAttendance || loadingDrivers || loadingIncidents;

  // Données formatées pour les graphiques
  const punctualityByCircuit = (punctualityData?.by_route || []).map((r) => ({
    name: r.name,
    onTime: r.on_time,
    delayed: r.total - r.on_time,
    rate: r.rate ?? null,
  }));

  const sortedByRate =
    punctualityByCircuit.length > 0 ? [...punctualityByCircuit].sort((a, b) => (b.rate ?? 0) - (a.rate ?? 0)) : [];
  const bestCircuitInfo = sortedByRate[0] ?? null;
  const worstCircuitInfo = sortedByRate.length > 1 ? sortedByRate[sortedByRate.length - 1] : null;

  const incidentTypes = incidentsData?.summary?.by_type
    ? Object.entries(incidentsData.summary.by_type).map(([name, value]) => ({ name, value }))
    : INCIDENT_TYPES;

  const driverPerformance = (driversData?.drivers || []).slice(0, 10).map((d) => ({
    id: d.id,
    name: d.name,
    rating: d.completion_rate ?? 0,
    trips: d.trips,
  }));

  const circuits = punctualityData?.by_route?.map((r) => ({ id: r.name, name: r.name })) ?? [];

  return {
    dateRange,
    setDateRange,
    selectedCircuit,
    setSelectedCircuit,
    circuits,
    punctualityData,
    attendanceData,
    driversData,
    incidentsData,
    punctualityByCircuit,
    driverPerformance,
    incidentTypes,
    bestCircuitInfo,
    worstCircuitInfo,
    loading,
    refresh,
    handleExportPDF,
    handleExportExcel,
  };
};

export default useReports;
